/**
 * Audio settings persistence — localStorage key `sots-audio-v1`
 * (mirrors deck storage style in shared/deck/storage.ts).
 */

export type AudioBusId =
  | "master"
  | "music"
  | "ambient"
  | "state"
  | "sfx"
  | "ui"
  | "voice"
  | "lieVo";

export const AUDIO_BUS_IDS: readonly AudioBusId[] = [
  "master",
  "music",
  "ambient",
  "state",
  "sfx",
  "ui",
  "voice",
  "lieVo",
] as const;

/** Child buses (everything except master). */
export type AudioChildBusId = Exclude<AudioBusId, "master">;

export const AUDIO_CHILD_BUS_IDS: readonly AudioChildBusId[] = [
  "music",
  "ambient",
  "state",
  "sfx",
  "ui",
  "voice",
  "lieVo",
] as const;

export interface AudioSettings {
  version: 1;
  master: number;
  music: number;
  ambient: number;
  state: number;
  sfx: number;
  ui: number;
  voice: number;
  /** Adversary claim VO — kept much lower than Scripture voice */
  lieVo: number;
  muted: boolean;
  /** Minimal preset: UI + major stingers only; no continuous beds */
  minimal: boolean;
  /**
   * Spoken Word channel. Default true once VO clips ship (Proclaim pack).
   */
  voiceEnabled: boolean;
  /** Lie claim whispers (test). Separate from Scripture VO. */
  lieVoEnabled: boolean;
}

/** Bump when defaults change in a way that should re-apply for existing installs. */
export const AUDIO_SETTINGS_KEY = "sots-audio-v4";

/** Design defaults — audio on by default; VO on (Proclaim clips present). */
export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  version: 1,
  master: 0.85,
  /** Bed under combat — keep clearly audible */
  music: 0.65,
  ambient: 0.55,
  state: 0.5,
  sfx: 0.7,
  ui: 0.55,
  voice: 0.85,
  /** ~⅓ of Scripture VO so claims sit under the Word even before duck */
  lieVo: 0.28,
  muted: false,
  minimal: false,
  voiceEnabled: true,
  lieVoEnabled: true,
};

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/** Sanitize partial / stored JSON into a full AudioSettings. */
export function sanitizeAudioSettings(
  raw: unknown,
  fallback: AudioSettings = DEFAULT_AUDIO_SETTINGS,
): AudioSettings {
  const base = { ...fallback };
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;

  const num = (key: keyof AudioSettings, def: number): number => {
    const v = o[key];
    return typeof v === "number" ? clamp01(v) : def;
  };
  const bool = (key: keyof AudioSettings, def: boolean): boolean => {
    const v = o[key];
    return typeof v === "boolean" ? v : def;
  };

  return {
    version: 1,
    master: num("master", base.master),
    music: num("music", base.music),
    ambient: num("ambient", base.ambient),
    state: num("state", base.state),
    sfx: num("sfx", base.sfx),
    ui: num("ui", base.ui),
    voice: num("voice", base.voice),
    lieVo: num("lieVo", base.lieVo),
    muted: bool("muted", base.muted),
    minimal: bool("minimal", base.minimal),
    voiceEnabled: bool("voiceEnabled", base.voiceEnabled),
    lieVoEnabled: bool("lieVoEnabled", base.lieVoEnabled),
  };
}

export function loadAudioSettings(): AudioSettings {
  try {
    if (typeof localStorage === "undefined") return { ...DEFAULT_AUDIO_SETTINGS };
    const raw = localStorage.getItem(AUDIO_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_AUDIO_SETTINGS };
    return sanitizeAudioSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_AUDIO_SETTINGS };
  }
}

export function saveAudioSettings(settings: AudioSettings): void {
  try {
    if (typeof localStorage === "undefined") return;
    const clean = sanitizeAudioSettings(settings);
    localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(clean));
  } catch {
    /* ignore quota / private mode */
  }
}

export function mergeAudioSettings(
  current: AudioSettings,
  partial: Partial<AudioSettings>,
): AudioSettings {
  return sanitizeAudioSettings({ ...current, ...partial, version: 1 as const });
}

/** Effective linear gain for a bus after master mute. */
export function effectiveBusGain(
  settings: AudioSettings,
  bus: AudioBusId,
): number {
  if (settings.muted) return 0;
  if (bus === "master") return settings.master;
  const child = settings[bus];
  return settings.master * child;
}

/**
 * Debug mute from URL: `?audio=0` forces muted without wiping saved prefs.
 * Call from prototype main when constructing the engine.
 */
export function audioDisabledByQuery(
  search: string = typeof location !== "undefined" ? location.search : "",
): boolean {
  try {
    const q = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
    const v = q.get("audio");
    return v === "0" || v === "false" || v === "off";
  } catch {
    return false;
  }
}
